// Real per-inverter panel for the station card (R6): live cards + 3-phase table
// + per-inverter day history (power, voltage, current). Data via /scada/inverters.
import i18n from '@/i18n';
import { useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { SectionCard } from '@/components/scada/kit';
import { scada, stateColor, type OpState } from '@/scada/tokens';
import { useChartTheme } from '@/components/base/chartTheme';
import ChartLegend from '@/components/base/ChartLegend';
import { useInverterLatest, useInverterDay } from '@/scada/useInverters';

const f1 = (v: number | null | undefined) => (v == null ? '—' : Number(v).toFixed(1));
const dot = (s: OpState) => <span style={{ width: 8, height: 8, borderRadius: 999, background: stateColor(s), display: 'inline-block' }} />;

export default function InverterPanel({ assetId }: { assetId?: string }) {
  const ct = useChartTheme();
  const { data, meta, err } = useInverterLatest(assetId);
  const { series } = useInverterDay(assetId);
  const [sel, setSel] = useState<number | null>(null);

  const selLatest = data?.find((d) => d.unitId === sel) ?? null;
  const selSeries = useMemo(() => series?.find((s) => s.unitId === sel)?.points ?? [], [series, sel]);

  if (err || meta?.mode === 'none') {
    return <SectionCard title={i18n.t('ak_scada2_inverters')}><span style={{ color: scada.textMuted, fontSize: 13 }}>{i18n.t('ak_scada2_inverter_data_is_not_available_for_thi')}</span></SectionCard>;
  }
  if (!data || !meta) return <SectionCard title={i18n.t('ak_scada2_inverters')}><span style={{ color: scada.textMuted, fontSize: 13 }}>{i18n.t('ak_scada2_loading')}</span></SectionCard>;

  // Станції з агрегованим обліком (SMA Data Manager): контролер віддає лише
  // сумарну потужність. Раніше тут малювалась сітка «інверторів», яку бекенд
  // домальовував рівномірним поділом суми (synthetic:true) — стільки карток,
  // скільки інверторів за паспортом. Жодна з них не відповідала реальному
  // пристрою, частина світилась «офлайн». Тепер показуємо чесний підсумок по
  // групі, без вигаданої розбивки.
  if (meta.mode === 'aggregate') {
    return (
      <SectionCard title={i18n.t('ak_scada2_inverters')} subtitle={i18n.t('ak_scada2_aggregated_metering_per_inverter_data')}>
        <div className="flex items-center gap-2 flex-wrap" style={{ background: `${scada.state.curtailed}12`, border: `1px solid ${scada.state.curtailed}33`, borderRadius: 8, padding: '9px 13px' }}>
          <i className="ri-server-line" style={{ color: scada.state.curtailed }} />
          <span style={{ color: scada.text, fontSize: 13, fontWeight: 600 }}>{meta.groupName ?? 'SMA Data Manager'}</span>
          <span style={{ color: scada.textMuted, fontSize: 12 }}>
            {i18n.t('ak_scada2_running_total_mw', { a: meta.availableInverters ?? '—', e: meta.expectedInverters ?? '—', p: meta.plantPacKw != null ? (meta.plantPacKw / 1000).toFixed(2) : '—' })}
          </span>
        </div>
        <div style={{ color: scada.textMuted, fontSize: 12, marginTop: 12 }}>
          {i18n.t('ak_scada2_the_station_controller_sends_only_a_to')}
        </div>
      </SectionCard>
    );
  }

  // Те саме правило, що й у смузі стану: в сітку потрапляють лише реальні
  // пристрої, домальовані бекендом — ні.
  const real = data.filter((d) => !d.synthetic);
  const working = real.filter((d) => d.status === 'ok').length;

  // ── Comparison: median of the group + deviation per inverter ──────────────
  const pcts = real.map((d) => d.percentOfNominal).filter((x): x is number => x != null);
  const median = pcts.length ? [...pcts].sort((a, b) => a - b)[Math.floor(pcts.length / 2)] : null;
  const devOf = (inv: typeof data[number]) =>
    median != null && inv.percentOfNominal != null ? inv.percentOfNominal - median : null;
  const heatColor = (inv: typeof data[number]) => {
    if (inv.status === 'offline') return scada.state.offline;
    const d = devOf(inv);
    if (d == null) return scada.textMuted;
    if (d >= -3) return scada.state.ok;
    if (d >= -10) return scada.state.warning;
    return scada.state.alarm;
  };
  const laggards = real.filter((inv) => { const d = devOf(inv); return d != null && d < -10; });

  return (
    <>
      <SectionCard
        title={i18n.t('ak_scada2_inverters_running', { w: working, n: real.length })}
        subtitle={i18n.t('ak_scada2_heat_map_by_deviation_from_the_group_m')}
        right={median != null ? <span style={{ color: scada.textMuted, fontSize: 11 }}>{i18n.t('ak_scada2_median_2', { m: median })}</span> : undefined}
      >
        {/* Comparison banner */}
        {laggards.length > 0 ? (
          <div style={{ background: `${scada.state.alarm}14`, border: `1px solid ${scada.state.alarm}40`, color: scada.state.alarm, borderRadius: 8, padding: '7px 12px', fontSize: 12, marginBottom: 12 }}>
            <i className="ri-alert-line" style={{ marginRight: 6 }} />
            {i18n.t('ak_scada2_lagging_behind_the_group')} {laggards.map((l) => `INV-${String(l.unitId).padStart(2, '0')} (${devOf(l)!.toFixed(0)}%)`).join(', ')}
          </div>
        ) : (
          <div style={{ color: scada.textMuted, fontSize: 12, marginBottom: 12 }}>
            <i className="ri-check-line" style={{ marginRight: 6, color: scada.state.ok }} />{i18n.t('ak_scada2_all_inverters_are_within_the_norm_rela')}
          </div>
        )}

        {/* Heatmap grid */}
        <div className="grid gap-2 grid-cols-3 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8">
          {real.map((inv) => {
            const c = heatColor(inv);
            const dev = devOf(inv);
            return (
              <button key={inv.unitId} onClick={() => setSel(inv.unitId)}
                style={{ textAlign: 'center', background: `${c}1A`, border: `1px solid ${inv.unitId === sel ? scada.accent : c + '55'}`, borderTop: `3px solid ${c}`, borderRadius: 8, padding: '8px 6px', cursor: 'pointer' }}>
                <div style={{ color: scada.text, fontSize: 12, fontWeight: 600 }}>INV-{String(inv.unitId).padStart(2, '0')}</div>
                <div style={{ color: c, fontSize: 18, fontWeight: 700, lineHeight: 1.1, marginTop: 2 }}>{inv.percentOfNominal != null ? `${inv.percentOfNominal}%` : '—'}</div>
                <div style={{ color: scada.textDim, fontSize: 11 }}>{f1(inv.pacKw)} {i18n.t('ak_scada2_kw')}</div>
                <div style={{ color: dev == null ? scada.textMuted : dev >= -3 ? scada.textMuted : c, fontSize: 10, marginTop: 2 }}>
                  {dev == null ? (inv.status === 'offline' ? i18n.t('ak_scada2_offline') : '—') : i18n.t('ak_scada2_vs_median', { s: dev > 0 ? '+' : '', d: dev.toFixed(0) })}
                </div>
              </button>
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-3 flex-wrap" style={{ marginTop: 10 }}>
          {[
            { c: scada.state.ok, t: i18n.t('ak_scada2_normal_3') },
            { c: scada.state.warning, t: i18n.t('ak_scada2_below_3_10') },
            { c: scada.state.alarm, t: i18n.t('ak_scada2_lagging_10') },
            { c: scada.state.offline, t: i18n.t('ak_scada2_offline') },
          ].map((l) => (
            <span key={l.t} className="inline-flex items-center gap-1.5" style={{ color: scada.textMuted, fontSize: 10 }}>
              <span style={{ width: 9, height: 9, borderRadius: 2, background: l.c, display: 'inline-block' }} />{l.t}
            </span>
          ))}
        </div>
      </SectionCard>

      {selLatest && (
        <div style={{ marginTop: 16 }}>
          <SectionCard
            title={i18n.t('ak_scada2_inv_details', { n: String(selLatest.unitId).padStart(2, '0') })}
            subtitle={i18n.t('ak_scada2_updated', { t: new Date(selLatest.readAtLocal).toLocaleTimeString(i18n.language === 'en' ? 'en-GB' : 'uk-UA') })}
            right={<button onClick={() => setSel(null)} style={{ background: 'transparent', border: 'none', color: scada.textMuted, cursor: 'pointer', fontSize: 16 }}><i className="ri-close-line" /></button>}
          >
            {/* Live 3-phase table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', minWidth: 360 }}>
                <thead>
                  <tr style={{ background: scada.panel2 }}>
                    {[i18n.t('ak_scada2_phase'), i18n.t('ak_scada2_voltage_v'), i18n.t('ak_scada2_current_a'), i18n.t('ak_scada2_power_kw')].map((h) => (
                      <th key={h} style={{ textAlign: 'left', color: scada.textMuted, fontSize: 11, fontWeight: 600, padding: '7px 14px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(['a', 'b', 'c'] as const).map((ph) => (
                    <tr key={ph} style={{ borderTop: `1px solid ${scada.border}` }}>
                      <td style={{ padding: '6px 14px', color: scada.text, fontSize: 13, fontWeight: 600 }}>{ph.toUpperCase()}</td>
                      <td style={{ padding: '6px 14px', color: scada.textDim, fontSize: 13 }}>{f1(selLatest.phases[ph].v)}</td>
                      <td style={{ padding: '6px 14px', color: scada.textDim, fontSize: 13 }}>{f1(selLatest.phases[ph].i)}</td>
                      <td style={{ padding: '6px 14px', color: scada.textDim, fontSize: 13 }}>{f1(selLatest.phases[ph].p)}</td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: `1px solid ${scada.borderStrong}`, background: scada.panel2 }}>
                    <td style={{ padding: '6px 14px', color: scada.text, fontSize: 12, fontWeight: 700 }}>Σ / DC</td>
                    <td style={{ padding: '6px 14px', color: scada.textMuted, fontSize: 12 }}>{f1(selLatest.facHz)} {i18n.t('ak_scada2_hz')}</td>
                    <td style={{ padding: '6px 14px', color: scada.textMuted, fontSize: 12 }}>DC {f1(selLatest.ppvKw)} {i18n.t('ak_scada2_kw')}</td>
                    <td style={{ padding: '6px 14px', color: scada.text, fontSize: 12, fontWeight: 700 }}>AC {f1(selLatest.pacKw)} {i18n.t('ak_scada2_kw')}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* History: power */}
            {selSeries.length > 0 && (
              <>
                <div style={{ color: scada.textMuted, fontSize: 11, margin: '14px 0 4px' }}>{i18n.t('ak_scada2_power_per_day_kw')}</div>
                <div style={{ width: '100%', height: 200 }}>
                  <ResponsiveContainer>
                    <LineChart data={selSeries} margin={{ top: 6, right: 8, bottom: 0, left: -12 }}>
                      <CartesianGrid {...ct.gridProps} />
                      <XAxis dataKey="t" {...ct.axis} minTickGap={28} />
                      <YAxis {...ct.axis} />
                      <Tooltip {...ct.tooltip} cursor={ct.cursorLine} />
                      <Line dataKey="pac" name="AC" stroke={ct.series.ok} {...ct.line} />
                      <Line dataKey="ppv" name="DC" stroke={ct.series.s3} {...ct.lineThin} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div style={{ color: scada.textMuted, fontSize: 11, margin: '12px 0 4px' }}>{i18n.t('ak_scada2_phase_voltage_per_day_v')}</div>
                <div style={{ width: '100%', height: 180 }}>
                  <ResponsiveContainer>
                    <LineChart data={selSeries} margin={{ top: 6, right: 8, bottom: 0, left: -12 }}>
                      <CartesianGrid {...ct.gridProps} />
                      <XAxis dataKey="t" {...ct.axis} minTickGap={28} />
                      <YAxis domain={['auto', 'auto']} {...ct.axis} />
                      <Tooltip {...ct.tooltip} cursor={ct.cursorLine} />
                      <Line dataKey="va" name="Uₐ" stroke={ct.series.s3} {...ct.lineThin} />
                      <Line dataKey="vb" name="U_b" stroke={ct.series.warn} {...ct.lineThin} />
                      <Line dataKey="vc" name="U_c" stroke={ct.series.s2} {...ct.lineThin} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <ChartLegend items={[
                  { label: 'Uₐ', color: ct.series.s3 },
                  { label: 'U_b', color: ct.series.warn },
                  { label: 'U_c', color: ct.series.s2 },
                ]} />

                <div style={{ color: scada.textMuted, fontSize: 11, margin: '12px 0 4px' }}>{i18n.t('ak_scada2_phase_current_per_day_a')}</div>
                <div style={{ width: '100%', height: 180 }}>
                  <ResponsiveContainer>
                    <LineChart data={selSeries} margin={{ top: 6, right: 8, bottom: 0, left: -12 }}>
                      <CartesianGrid {...ct.gridProps} />
                      <XAxis dataKey="t" {...ct.axis} minTickGap={28} />
                      <YAxis {...ct.axis} />
                      <Tooltip {...ct.tooltip} cursor={ct.cursorLine} />
                      <Line dataKey="ia" name="Iₐ" stroke={ct.series.s3} {...ct.lineThin} />
                      <Line dataKey="ib" name="I_b" stroke={ct.series.warn} {...ct.lineThin} />
                      <Line dataKey="ic" name="I_c" stroke={ct.series.s2} {...ct.lineThin} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <ChartLegend items={[
                  { label: 'Iₐ', color: ct.series.s3 },
                  { label: 'I_b', color: ct.series.warn },
                  { label: 'I_c', color: ct.series.s2 },
                ]} />
              </>
            )}
          </SectionCard>
        </div>
      )}
    </>
  );
}
